import {apiRequest} from "@/lib/api-client";

export type SupportTicketCategory = "general" | "technical" | "account" | "billing" | "bug" | "feature";
export type SupportTicketPriority = "low" | "normal" | "high" | "urgent";
export type SupportTicketStatus = "open" | "in_progress" | "waiting_user" | "closed";

export interface SupportTicketMessage {
    uid: string;
    senderUserId: number;
    senderDisplayName: string;
    senderAvatarUrl: string | null;
    senderType: "user" | "staff" | string;
    content: string;
    isInternal: boolean;
    createdAt: string;
}

export interface SupportTicketAttachment {
    uid: string;
    uploadedByUserId: number;
    uploadedByDisplayName: string;
    filePath: string;
    originalFilename: string;
    mimeType: string;
    createdAt: string;
}

export interface SupportTicket {
    uid: string;
    title: string;
    description: string;
    category: SupportTicketCategory;
    priority: SupportTicketPriority;
    status: SupportTicketStatus;
    creatorUserId: number;
    creatorUid: string;
    creatorDisplayName: string;
    creatorAvatarUrl: string | null;
    workspaceUid: string | null;
    workspaceName: string | null;
    assignedUserId: number | null;
    assignedDisplayName: string | null;
    messageCount: number;
    messages: SupportTicketMessage[];
    attachments: SupportTicketAttachment[];
    createdAt: string;
    updatedAt: string;
    closedAt: string | null;
}

interface BaseResponse {
    isOk: boolean;
    status: string;
    message: string;
}

export interface SupportTicketListResponse extends BaseResponse {
    tickets: SupportTicket[];
    total: number;
    canManage: boolean;
}

export interface SupportTicketResponse extends BaseResponse {
    ticket: SupportTicket;
    canManage: boolean;
}

interface SupportTicketMessageResponse extends BaseResponse {
    ticketMessage: SupportTicketMessage;
}

interface SupportTicketAttachmentResponse extends BaseResponse {
    attachment: SupportTicketAttachment;
}

export interface CreateSupportTicketInput {
    title: string;
    description: string;
    category: SupportTicketCategory;
    priority: SupportTicketPriority;
}

export interface UpdateSupportTicketInput {
    status?: SupportTicketStatus;
    priority?: SupportTicketPriority;
    assignedUserId?: number | null;
}

function ticketPath(ticketUid: string): string {
    return `/support/tickets/${encodeURIComponent(ticketUid)}`;
}

export function listSupportTickets(
    scope: "mine" | "all" = "mine",
    status?: SupportTicketStatus | "",
): Promise<SupportTicketListResponse> {
    const query = new URLSearchParams({scope});
    if (status) {
        query.set("status", status);
    }
    return apiRequest<SupportTicketListResponse>({
        path: `/support/tickets?${query.toString()}`,
    });
}

export function getSupportTicket(ticketUid: string): Promise<SupportTicketResponse> {
    return apiRequest<SupportTicketResponse>({path: ticketPath(ticketUid)});
}

export function createSupportTicket(input: CreateSupportTicketInput): Promise<SupportTicketResponse> {
    return apiRequest<SupportTicketResponse>({
        path: "/support/tickets",
        method: "POST",
        body: input,
    });
}

export function addSupportTicketMessage(
    ticketUid: string,
    content: string,
): Promise<SupportTicketMessageResponse> {
    return apiRequest<SupportTicketMessageResponse>({
        path: `${ticketPath(ticketUid)}/messages`,
        method: "POST",
        body: {content},
    });
}

export function updateSupportTicket(
    ticketUid: string,
    input: UpdateSupportTicketInput,
): Promise<SupportTicketResponse> {
    return apiRequest<SupportTicketResponse>({
        path: ticketPath(ticketUid),
        method: "PATCH",
        body: input,
    });
}

export function uploadSupportTicketAttachment(
    ticketUid: string,
    file: File,
): Promise<SupportTicketAttachmentResponse> {
    const formData = new FormData();
    formData.append("file", file);
    return apiRequest<SupportTicketAttachmentResponse>({
        path: `${ticketPath(ticketUid)}/attachments`,
        method: "POST",
        formData,
    });
}

export function supportAttachmentUrl(filePath: string): string {
    const configured = (process.env.NEXT_PUBLIC_API_URI ?? "").trim();
    const apiBase = configured || "http://localhost:8000/api/v1";
    const normalizedFile = filePath.replace(/^\/+/, "");

    try {
        const url = new URL(apiBase);
        const rootPath = url.pathname.replace(/\/api\/v1\/?$/, "").replace(/\/$/, "");
        return `${url.origin}${rootPath}/${normalizedFile}`;
    } catch {
        return `/${normalizedFile}`;
    }
}
